import React, { useState, useEffect } from "react";
import SupportTypes from "./data/supportTypes.json";
import SuccessModal from "./SuccessModal";
import ExperienceModal from "./ExperienceModal";
import { useOutletContext } from "react-router-dom";
import { FaAngleLeft } from "react-icons/fa";
import config from "../../../config/config";
import axios from "axios";

const ExperienceComponent = ({ user }) => {
  const [experienceAreas, setExperienceAreas] = useState(
    SupportTypes.map((category) => ({
      ...category,
      skills: category.options.map((skill) => ({
        name: skill,
        professional: false,
        personal: false,
        showExperienceTypeSelection: false,
      })),
      isSelected: false,
    }))
  );

  const [openDivs, setOpenDivs] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [experienceJSON, setExperienceJSON] = useState([]);
  const [showError, setShowError] = useState(false);
  const [currentSelection, setCurrentSelection] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({});

  const baseURL = config.API_BASE_URL;
  const { isSmallScreen, handleBackClick } = useOutletContext();

  useEffect(() => {
    const fetchExperienceData = async () => {
      if (user && user.id) {
        try {
          const response = await axios.get(
            `${baseURL}/v1/worker-users/${user.id}`
          );
          const fetchedUserData = response.data;
          const fetchedExperienceData = fetchedUserData.experience.experiences;

          if (fetchedExperienceData && fetchedExperienceData.length > 0) {
            const updatedExperienceAreas = SupportTypes.map((category) => {
              const fetchedCategory = fetchedExperienceData.find(
                (cat) => cat.discipline === category.text
              );
              return {
                ...category,
                skills: category.options.map((skill) => {
                  const fetchedSkill = fetchedCategory?.skills.find(
                    (sk) => sk.name === skill
                  );
                  return {
                    name: skill,
                    professional: fetchedSkill?.professional || false,
                    personal: fetchedSkill?.personal || false,
                    showExperienceTypeSelection: fetchedSkill ? true : false,
                  };
                }),
                isSelected: !!fetchedCategory,
              };
            });

            setExperienceAreas(updatedExperienceAreas);
          }
        } catch (error) {
          console.error("Failed to fetch user experience data:", error);
        }
      }
    };

    fetchExperienceData();
  }, [user, baseURL]);

  useEffect(() => {
    validateSelections();
    recordSelections();
  }, [experienceAreas]);

  useEffect(() => {
    setOpenDivs((prev) => {
      if (currentSelection != null) {
        return [currentSelection];
      }
      return prev;
    });
  }, [currentSelection]);

  const anySelectionMade = experienceAreas.some((area) => area.isSelected);

  const openModalForAreaSelection = (areaText) => {
    const areaIndex = experienceAreas.findIndex(
      (area) => area.text === areaText
    );
    if (areaIndex !== -1) {
      setModalContent({ ...experienceAreas[areaIndex], areaIndex });
      setIsModalOpen(true);
    }
  };

  const handleModalSelection = ({ areaIndex, updatedSkills }) => {
    const newExperienceAreas = experienceAreas.map((area, idx) => {
      if (idx === areaIndex) {
        const isAnySkillSelected = updatedSkills.some(
          (skill) => skill.professional || skill.personal
        );
        return {
          ...area,
          skills: updatedSkills,
          isSelected: isAnySkillSelected,
        };
      }
      return area;
    });

    setExperienceAreas(newExperienceAreas);
  };

  const validateSelections = () => {
    const isAnyAreaSelected = experienceAreas.some((area) => area.isSelected);

    const areSkillsValid = experienceAreas.every(
      (area) =>
        !area.isSelected || (area.isSelected && isAnySkillSelected(area.skills))
    );

    const valid = isAnyAreaSelected && areSkillsValid;
    setIsValid(valid);
  };

  const recordSelections = () => {
    const selectedAreas = experienceAreas
      .filter((area) => area.isSelected && isAnySkillSelected(area.skills))
      .map((area) => ({
        Discipline: area.text,
        Skills: area.skills
          .filter((skill) => skill.professional || skill.personal)
          .map((skill) => ({
            Name: skill.name,
            Professional: skill.professional,
            Personal: skill.personal,
          })),
      }));

    setExperienceJSON(selectedAreas);
  };

  const handleSave = async () => {
    let incompleteSelections = [];
    experienceAreas.forEach((area, index) => {
      if (area.isSelected && !isAnySkillSelected(area.skills)) {
        incompleteSelections.push(index);
      }
    });

    if (incompleteSelections.length > 0) {
      setOpenDivs(incompleteSelections);
      setShowError(true);
    } else {
      validateSelections();
      recordSelections();
      if (isValid) {
        console.log("Experience:", JSON.stringify(experienceJSON, null, 2));
        console.log("Form is valid:", isValid);
        setShowError(false);
        setOpenDivs([]);
        const experienceData = experienceJSON.map((exp) => ({
          discipline: exp.Discipline,
          skills: exp.Skills.map((skill) => ({
            name: skill.Name,
            professional: skill.Professional,
            personal: skill.Personal,
          })),
        }));

        try {
          console.log("Experience Data to be saved: ", experienceData);
          const updateUserResponse = await axios.patch(
            `${baseURL}/v1/worker-users/${user.id}`,
            {
              experience: experienceData,
            }
          );

          if (updateUserResponse.data) {
            console.log("Patch successful");
            setShowModal(true);
          }
        } catch (error) {
          console.error("Failed to patch user data:", error);
        }
      } else {
        console.error(
          "Validation error: Please make at least one selection in each selected area before saving."
        );
        setShowError(true);
      }
    }
  };

  const isAnySkillSelected = (skills) => {
    return skills.some((skill) => skill.professional || skill.personal);
  };

  const closeModal = (areaIndex) => {
    setIsModalOpen(false);
    const newExperienceAreas = experienceAreas.map((area, idx) => {
      if (idx === areaIndex) {
        const isAnySkillSelected = area.skills.some(
          (skill) => skill.professional || skill.personal
        );
        return { ...area, isSelected: isAnySkillSelected };
      }
      return area;
    });

    setExperienceAreas(newExperienceAreas);

    const anyAreasSelected = newExperienceAreas.some((area) => area.isSelected);

    if (!anyAreasSelected) {
      setOpenDivs([]);
    } else {
      setOpenDivs((prevOpenDivs) => [...new Set([...prevOpenDivs, areaIndex])]);
    }
  };

  return (
    <div className="experience-custom-component-container">
      {isModalOpen && (
        <ExperienceModal
          content={modalContent}
          onClose={closeModal}
          onSelection={handleModalSelection}
        />
      )}
      {showModal && <SuccessModal />}
      <div className="experience-indicative-above-div1">
      <div className="profile-back-container">
        {isSmallScreen && (
            <div className="profile-back-arrow" onClick={handleBackClick}>
              <FaAngleLeft className="profile-back-icon"/> 
            </div>
          )}
          <h2>Experience</h2>
        </div>
        
        <p>
          Select all areas that you’ve worked or have professional or personal
          experience in.
        </p>
      </div>
      <div className="experience-columns-wrapper">
        <div className="experience-left-column">
          <div className="area-buttons-container">
            {experienceAreas.map((area, index) => (
              <button
                key={area.text}
                className={`area-button ${
                  area.isSelected &&
                  area.skills.some(
                    (skill) => skill.professional || skill.personal
                  )
                    ? "selected"
                    : ""
                }`}
                onClick={() => openModalForAreaSelection(area.text)}
              >
                <div className="button-checkmark">
                  {area.isSelected &&
                  area.skills.some(
                    (skill) => skill.professional || skill.personal
                  )
                    ? "✓"
                    : " "}
                </div>
                <div className="button-text">{area.text}</div>
              </button>
            ))}
          </div>
        </div>
        {anySelectionMade && (
          <div className="experience-right-column">
            <h3 className="experience-right-column-h3">Areas & Skills</h3>
            {experienceAreas
              .filter(
                (area) =>
                  area.isSelected &&
                  area.skills.some(
                    (skill) => skill.professional || skill.personal
                  )
              )
              .map((area, areaIndex) => (
                <div
                  key={area.text}
                  className="experience-selected-area"
                  onClick={() => openModalForAreaSelection(area.text)}
                >
                  <h3>{area.text}</h3>
                  <div
                    className={`skills ${
                      openDivs.includes(areaIndex) ? "expanded" : ""
                    }`}
                  >
                    {area.skills
                      .filter((skill) => skill.professional || skill.personal)
                      .map((skill, skillIndex) => (
                        <div key={skillIndex} className="skill-container">
                          <button
                            className={`skill-button ${
                              skill.professional ? "professional" : ""
                            } ${skill.personal ? "personal" : ""}`}
                          >
                            {skill.name}
                          </button>
                          <div className="skill-hover-info">
                            Skill Level :{" "}
                            {skill.professional && skill.personal
                              ? "Professional, Personal"
                              : skill.professional
                              ? "Professional"
                              : "Personal"}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
      {showError && (
        <div className="experience-error">
          Please make at least one selection in each selected area before
          saving.
        </div>
      )}
      <div className="experience-save">
        <button onClick={handleSave}>Save and continue</button>
      </div>
    </div>
  );
};

export default ExperienceComponent;
